import { z } from 'zod';

/**
 * Состояние перечня форм, как его несёт адресная строка (`rules/lists-and-filters.mdc` §1).
 *
 * **`GET /templates` не постранично** («каталог печатных форм мал по построению», контракт) — поэтому
 * `page` и `sort` применяются к уже полученному ответу, тем же приёмом, каким `units/team` разбирает
 * свой нефильтруемый на сервере перечень (`team-list-search.schema.ts`). Единственный параметр,
 * который действительно уходит на сервер, — `inCirculation`: он меняет сам запрос (другой кеш,
 * другая query-key), поэтому здесь он называется `circulation` и переводится в `boolean | undefined`
 * местом вызова, а не хранится как булево прямо в адресе — `?circulation=active` читается человеком,
 * `?inCirculation=true` тоже, но два похожих флага (клиентский текстовый и серверный булев) с одним
 * именем расходились бы в дальнейшем правками порознь.
 *
 * `.catch(...)`, не `throw`: адрес приходит из общей ссылки, надписанной руками закладки, и экран,
 * падающий на `?circulation=bogus`, — это экран, падающий на ссылку, которую кто-то переслал коллеге.
 */

export const TEMPLATE_SORTS = ['title', '-title', 'createdAt', '-createdAt'] as const;

export type TemplateSort = (typeof TEMPLATE_SORTS)[number];

export const TEMPLATE_CIRCULATION_FILTERS = ['all', 'active', 'withdrawn'] as const;

export type TemplateCirculationFilter = (typeof TEMPLATE_CIRCULATION_FILTERS)[number];

/** Столько же, сколько допускает наименование формы, — искать длиннее нечего. */
const MAX_QUERY = 300;

export const templateRegistrySearchSchema = z.object({
  q: z.string().trim().max(MAX_QUERY).catch(''),
  circulation: z.enum(TEMPLATE_CIRCULATION_FILTERS).catch('all'),
  sort: z.enum(TEMPLATE_SORTS).catch('-createdAt'),
  page: z.coerce.number().int().min(1).catch(1),
});

export type TemplateRegistrySearch = z.infer<typeof templateRegistrySearchSchema>;

/** `circulation` из адреса → параметр операции `GET /templates`. `'all'` не фильтрует ничем. */
export const inCirculationParamOf = (
  circulation: TemplateCirculationFilter,
): boolean | undefined => {
  if (circulation === 'active') return true;
  if (circulation === 'withdrawn') return false;

  return undefined;
};
